import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ApiError, api } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import { AlreadySignedInNotice } from '../../components/AlreadySignedInNotice'

// Step 2 ("Verify account") is architecture-only: User.email_verified_at
// exists on the backend and the verification email does send, but nothing
// blocks login or routing on it being set — verification is a
// discoverability signal, not a login gate, by design.
// Step 3 ("Begin intake") is the redirect below — signup IS the entry into
// the same patient-scoped intake/matching flow, not a separate screen.
export default function FindCare() {
  const navigate = useNavigate()
  const { me, loginWithToken } = useAuth()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (!acceptedTerms) {
      setError('You must agree to the Terms of Service and acknowledge the Privacy Policy to continue.')
      return
    }
    setBusy(true)
    try {
      const result = await api('/patient-auth/signup', {
        method: 'POST',
        body: JSON.stringify({ first_name: firstName, last_name: lastName, email, password, accepted_terms: acceptedTerms }),
      })
      await loginWithToken(result.access_token)
      navigate('/patient/intake', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 409 ? 'An account with this email already exists.' : 'Could not create your account.')
      } else {
        setError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  if (me) return <AlreadySignedInNotice continueAs="a patient" />

  return (
    <div className="auth-shell warm-theme">
      <div className="auth-card">
        <h1>Start your intake</h1>
        <p className="muted" style={{ fontSize: 13.5 }}>
          Create an account to complete a structured intake and be routed to providers who better fit your
          clinical and practical needs.
        </p>
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="field"><label>First name</label><input className="inp" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus /></div>
            <div className="field"><label>Last name</label><input className="inp" value={lastName} onChange={(e) => setLastName(e.target.value)} required /></div>
          </div>
          <div className="field"><label>Email</label><input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div className="field"><label>Password</label><input className="inp" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} /></div>
          <div className="field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
            <input type="checkbox" id="acceptedTerms" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} style={{ marginTop: 3 }} aria-required="true" />
            <label htmlFor="acceptedTerms" style={{ margin: 0, fontSize: 13.5 }}>
              I have read and agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and acknowledge the <Link to="/privacy" target="_blank">Privacy Policy</Link>.
            </label>
          </div>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="btn full" type="submit" disabled={busy}>{busy ? 'Creating your account…' : 'Create Account & Start Intake'}</button>
        </form>
        <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
        <p className="legal-note" style={{ marginTop: 14 }}>
          ATUUNE routes you to a credentialed provider — it does not provide medical advice, therapy, or
          emergency care. Not for emergencies.
        </p>
      </div>
    </div>
  )
}
